import React from "react";
import { getLessonsFromLocalStorage } from '../../utils/lessonsService';

export default function UpcomingLessonsTab({
  groupId,
  groupData,
  weekOffset, setWeekOffset,
  selectedDayIndex, setSelectedDayIndex,
  handleLessonStatusChange,
}) {
  // Получаем все уроки группы для weekly view
  const allLessonsData = getLessonsFromLocalStorage();
  const allGroupLessons = allLessonsData.filter(lesson => lesson.groupId === groupId);

  if (allGroupLessons.length > 0) {
    // Build current week (Mon-Sun) without past lessons, like StudentProfile
    const today = new Date();
    const dayIndexMon0 = (today.getDay() + 6) % 7;
    const weekStart = new Date(today);
    weekStart.setDate(today.getDate() - dayIndexMon0 + (weekOffset * 7));
    weekStart.setHours(0, 0, 0, 0);
    const weekDays = Array.from({ length: 7 }, (_, i) => {
      const d = new Date(weekStart);
      d.setDate(weekStart.getDate() + i);
      return d;
    });
    const weekEnd = new Date(weekDays[6]);
    weekEnd.setHours(23, 59, 59, 999);

    // Get all lessons for this group (past and future)
    const allGroupLessonsData = getLessonsFromLocalStorage();
    const allLessons = allGroupLessonsData.filter(lesson => lesson.groupId === groupId);

    const lessonsInWeek = allLessons.filter(l => {
      if (!l.date) return false;
      const d = new Date(l.date);
      return d >= weekStart && d <= weekEnd;
    });
    const lessonsByDay = weekDays.map(d => {
      const y = d.getFullYear(), m = d.getMonth(), day = d.getDate();
      return lessonsInWeek
        .filter(l => { const ld = l.date ? new Date(l.date) : null; return ld && ld.getFullYear()===y && ld.getMonth()===m && ld.getDate()===day; })
        .sort((a,b)=> (a.from||'').localeCompare(b.from||''));
    });
    const weekdayFormatter = new Intl.DateTimeFormat('en-US', { weekday: 'short' });
    const dayNumFormatter = new Intl.DateTimeFormat('en-US', { day: '2-digit', month: 'short' });

    return (
      <div className="upcoming-lessons-content">
        <div className="week-nav">
          <h3>Upcoming Lessons</h3>
          <div className="week-navigation">
            <button
              onClick={() => setWeekOffset(prev => prev - 1)}
              className="week-nav-btn"
            >
              ‹
            </button>
            <button
              onClick={() => setWeekOffset(prev => prev + 1)}
              className="week-nav-btn"
            >
              ›
            </button>
          </div>
        </div>
        <div className="upcoming-lessons-week">
          <div className="week-grid">
            {weekDays.map((d, idx) => (
              <div key={idx} className="week-column">
                <div className="week-day-label">
                  <span className="week-day">{weekdayFormatter.format(d)}</span>
                  <span className="week-date">{dayNumFormatter.format(d)}</span>
                </div>
                <div className="week-lessons">
                  {lessonsByDay[idx].length === 0 ? (
                    <div className="week-empty">no lessons scheduled</div>
                  ) : (
                    lessonsByDay[idx].map(lesson => (
                      <div key={lesson.id} className={`week-lesson-chip status-${lesson.status || 'scheduled'}`}>
                        <div className="week-lesson-meta">
                          <div className="week-lesson-time">{lesson.from || ''}{lesson.to ? `–${lesson.to}` : ''}</div>
                          <div className="week-lesson-title">{groupData.name || 'Group'}</div>
                          <select
                            value={lesson.status || 'scheduled'}
                            onChange={(e) => handleLessonStatusChange(lesson.id, e.target.value)}
                            className="lesson-status-select compact"
                          >
                            <option value="scheduled">Scheduled</option>
                            <option value="in_progress">In Progress</option>
                            <option value="done">Done</option>
                            <option value="cancelled">Cancelled</option>
                          </select>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Mobile-only day-strip + timeline — same visual treatment
         * as StudentProfile.js's Class schedule redesign (which
         * itself matches the student dashboard's own schedule
         * widget): a horizontal strip of day pills instead of a
         * 7-column grid that's too cramped to read on a phone. */}
        <div className="teacher-week-mobile-view">
          <div className="upcoming-lessons-week-strip">
            {weekDays.map((d, idx) => {
              const hasLesson = lessonsByDay[idx].length > 0;
              const isToday = d.toDateString() === new Date().toDateString();
              return (
                <div
                  key={idx}
                  role="button"
                  tabIndex={0}
                  className={`upcoming-lessons-day ${hasLesson ? 'has-lesson' : ''} ${selectedDayIndex === idx ? 'selected' : ''} ${isToday ? 'is-today' : ''}`}
                  onClick={() => setSelectedDayIndex(idx)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedDayIndex(idx); } }}
                  aria-pressed={selectedDayIndex === idx}
                  aria-label={`${weekdayFormatter.format(d)} ${d.getDate()}${isToday ? ', today' : ''}${hasLesson ? ', has lessons' : ''}`}
                >
                  <span className="upcoming-lessons-day-circle" aria-hidden />
                  <span className="upcoming-lessons-day-label">{weekdayFormatter.format(d)}</span>
                  <span className="upcoming-lessons-day-num">{d.getDate()}</span>
                </div>
              );
            })}
          </div>
          <div className="upcoming-lessons-timeline">
            {selectedDayIndex == null ? (
              <p className="upcoming-lessons-pick-day">Click a day to see lessons</p>
            ) : lessonsByDay[selectedDayIndex].length === 0 ? (
              <p className="upcoming-lessons-no-lessons-day">No lessons on this day</p>
            ) : (
              lessonsByDay[selectedDayIndex].map(lesson => {
                const normalizedStatus = (lesson.status || 'scheduled').toLowerCase().replace(/\s+/g, '_');
                const statusClass = normalizedStatus === 'done' ? 'completed'
                  : normalizedStatus === 'in_progress' ? 'in-progress'
                  : normalizedStatus === 'cancelled' ? 'cancelled'
                  : 'upcoming';
                const statusLabel = statusClass === 'in-progress' ? 'in progress' : statusClass;
                return (
                  <div key={lesson.id} className="schedule-lesson-card">
                    <div className="schedule-lesson-left">
                      <span className="schedule-lesson-time">{lesson.from || ''}</span>
                      <span className="schedule-lesson-divider" aria-hidden />
                      <span className="schedule-lesson-date">{groupData.name || 'Group'}</span>
                    </div>
                    <span className={`schedule-lesson-status schedule-lesson-status--${statusClass}`}>
                      <span className={`schedule-lesson-status-icon schedule-lesson-status-icon--${statusClass}`} aria-hidden />
                      {statusLabel}
                    </span>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="upcoming-lessons-content">
      <h3>Upcoming Lessons</h3>
      <p className="no-data-message">No upcoming lessons scheduled.</p>
    </div>
  );
}
